import React, { useEffect, useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import { timeSheetValidationSchema } from "../../../components/Helpers";
import Hoc from "../../../components/HOC";
import ModalComponent from "../../../components/Modal";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import CustomDropdown from "../../../components/CustomDropdown";
import FormField from "../../../components/FormField";
import FormButton from "../../../components/FormButton";
import FormRow from "../../../components/FormRow";
import MuiDatePicker from "../../../components/MuiDatePicker";
import { toast } from "react-toastify";
import {
  FaCalendarAlt,
  FaClock,
  FaTrash,
  FaPlus,
  FaUpload,
  FaFileExcel,
  FaTimes,
  FaFile,
} from "react-icons/fa";
import { COLORS, TAILWIND_CLASSES } from "../../../Constants/theme";
import { closeTimesheetModal } from "../../../Redux/TimeSheetState/TimeSheetActionCreator";

const TimeSheet = ({
  onAddTimeSheet,
  title,
  timesheetValues,
  projectsList,
  isLoading,
}) => {
  // Filter to show only active projects
  const today = moment().startOf("day");
  const activeProjects = projectsList.filter((project) => {
    // If no endDate, consider as active
    if (!project.endDate) return true;
    // Check if project end date is today or in the future
    return moment(project.endDate, "MM-DD-YYYY").endOf("day").isSameOrAfter(today);
  });
  const [holidays, setHolidays] = useState([]);
  const timesheetByIdReducer = useSelector(
    (state) => state.GetTimesheetByIdReducer
  );
  const modalState = useSelector((state) => state.TimesheetModalReducer);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const dispatch = useDispatch();

  

  // Reset state when component mounts
  useEffect(() => {
    setHolidays([]);
    setSelectedFiles([]);
    setIsDragging(false);
  }, []);

  const addHoliday = () => {
    setHolidays([...holidays, { employee_holiday_date: "", notes: "" }]);
  };

  const removeHoliday = (index) => {
    const updatedHolidays = holidays.filter((_, i) => i !== index);
    setHolidays(updatedHolidays);
  };

  const handleSubmit = (values, { resetForm, setSubmitting }) => {
    try {
      setSubmitting(true);

      // Validate holidays
      const validHolidays = holidays.filter(
        (holiday) =>
          holiday.employee_holiday_date &&
          holiday.notes &&
          holiday.notes.trim().length >= 3
      );

      // Only validate if there are holidays
      if (holidays.length > 0 && validHolidays.length !== holidays.length) {
        toast.error(
          "Please fill in all holiday details or remove empty holidays"
        );
        setSubmitting(false);
        return;
      }

      const timeSheetData = {
        ...values,
        projectId: values.projectId?.id || values.projectId,
        startDate: moment(values.startDate, "MM-DD-YYYY").format("MM-DD-YYYY"), 
        endDate: moment(values.endDate, "MM-DD-YYYY").format("MM-DD-YYYY"),
        holidays: validHolidays,
      };

      onAddTimeSheet(timeSheetData, selectedFiles);

      // Reset holidays state after submission
      setHolidays([]);
      setSelectedFiles([]);

      // Set submission state
      setTimeout(() => {
        setSubmitting(false);
      }, 1000);
    } catch (error) {
      setSubmitting(false);
      toast.error("Error in submitting timesheet");
    }
  };

  const handleClose = () => {
    dispatch(closeTimesheetModal());
    setHolidays([]);
    setSelectedFiles([]);
    setIsDragging(false);
  };

  useEffect(() => {
    if (!timesheetByIdReducer?.Loading && timesheetByIdReducer?.Response) {
      try {
        const holidaysData = timesheetByIdReducer.Response.holidays || [];

        // Ensure holidays data has the required structure
        setHolidays(
          holidaysData.map((holiday) => ({
            employee_holiday_date: holiday.employee_holiday_date
              ? moment(holiday.employee_holiday_date, "MM-DD-YYYY").format(
                  "MM-DD-YYYY"
                )
              : "",
            notes: holiday.notes || "",
          }))
        );

        if (
          timesheetByIdReducer.Response.files &&
          timesheetByIdReducer.Response.files.length > 0
        ) {
          setSelectedFiles(timesheetByIdReducer.Response.files);
        }
      } catch (error) {}
    }
  }, [timesheetByIdReducer]);

  const updateHoliday = (index, field, value) => {
    const updatedHolidays = holidays.map((holiday, i) =>
      i === index
        ? {
            ...holiday,
            [field]:
              field === "employee_holiday_date"
                ? moment(value, "MM-DD-YYYY").format("MM-DD-YYYY")
                : value,
          }
        : holiday
    );
    setHolidays(updatedHolidays);
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter((file) => {
      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        toast.error(`File ${file.name} size should be less than 10MB`);
        return false;
      }

      // Check for duplicate file names
      const isDuplicate = selectedFiles.find(
        (existingFile) =>
          existingFile.name === file.name ||
          existingFile?.fileName === file.name
      );

      if (isDuplicate) {
        toast.error(`File "${file.name}" already exists.`);
        return false;
      }

      return true;
    });

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = Array.from(event.dataTransfer.files);
    const validFiles = files.filter((file) => {
      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        toast.error(`File ${file.name} size should be less than 10MB`);
        return false;
      }

      // Check for duplicate file names
      const isDuplicate = selectedFiles.find(
        (existingFile) =>
          existingFile.name === file.name ||
          existingFile?.fileName === file.name
      );

      if (isDuplicate) {
        toast.error(`File "${file.name}" already exists.`);
        return false;
      }

      return true;
    });

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDeleteFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <ModalComponent
      show={modalState.isOpen}
      onClose={handleClose}
      title={<span className="text-indigo-500">Add Timesheet</span>}
      className="w-full max-w-[95vw] sm:max-w-4xl mx-auto p-4 sm:p-6 max-h-[90vh] overflow-y-auto"
    >
      <Formik
        initialValues={{
          ...timesheetValues,
          projectId:
            typeof timesheetValues?.projectId === "object"
              ? timesheetValues?.projectId?.id
              : timesheetValues?.projectId,
        }}
        enableReinitialize
        validationSchema={timeSheetValidationSchema}
        validateOnChange={true}
        validateOnBlur={true}
        validateOnSubmit={true}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
          resetForm,
        }) => {
          const isProjectEnded =
            values?.endDate && moment().isAfter(moment(values?.endDate));
          return (
            <Form
              onSubmit={(e) => {
                e.preventDefault();

                // Validate holidays before submission
                const validHolidays = holidays.filter(
                  (holiday) =>
                    holiday.employee_holiday_date &&
                    holiday.notes &&
                    holiday.notes.trim().length >= 3
                );

                if (
                  holidays.length > 0 &&
                  validHolidays.length !== holidays.length
                ) {
                  toast.error(
                    "Please fill in all holiday details or remove empty holidays"
                  );
                  return;
                }

                // If all holidays are valid or there are no holidays, proceed with submission
                handleSubmit(e);
              }}
              className="p-2"
            >
              <div className="flex flex-col h-full overflow-y-auto max-h-[80vh]">
                {/* Project and Location Selection */}
                <FormRow
                  cols={timesheetValues?.status === "REJ" ? " sm:3" : " sm:2"}
                  gap={2}
                  className="flex-col sm:flex-row gap-2 sm:gap-4"
                >
                  <div className="relative w-full mt-2">
                    <CustomDropdown
                      value={activeProjects.find(
                        (project) => project.id === values?.projectId
                      )}
                      name="projectId"
                      customClasses="w-full text-left text-xs sm:text-sm"
                      handleChange={(selectedOption) => {
                        if (
                          selectedOption &&
                          selectedOption.endDate &&
                          moment().isAfter(moment(selectedOption.endDate))
                        ) {
                          toast.error("Start date exceeds project end date");
                          return;
                        }
                        setFieldValue("projectId", selectedOption.id);
                      }}
                      placeholder="Select Project"
                      options={activeProjects}
                      handleGetOptionValue={(option) => option.id}
                      handleGetOptionName={(option) => option.projectName}
                      isDisabled={title === "Edit Timesheet"}
                      required
                    />
                    {errors.projectId && touched.projectId && (
                      <div className="text-red-500 text-xs sm:text-sm mt-1 text-left">
                        {errors.projectId}
                      </div>
                    )}
                  </div>
                  <div className="relative w-full mt-2">
                    <CustomDropdown
                      value={[
                        { value: "Office", label: "Office" },
                        { value: "Remote", label: "Remote" },
                      ].find(
                        (work_location) =>
                          work_location.value === values?.work_location
                      )}
                      name="work_location"
                      customClasses="w-full text-left text-xs sm:text-sm"
                      handleChange={(selectedOption) => {
                        setFieldValue("work_location", selectedOption?.value);
                        // setFieldTouched('work_location', true);
                      }}
                      placeholder="Select work location"
                      options={[
                        { value: "Office", label: "Office" },
                        { value: "Remote", label: "Remote" },
                      ]}
                      handleGetOptionValue={(option) => option.value}
                      handleGetOptionName={(option) => option.label}
                      isDisabled={title === "Edit Timesheet"}
                      required
                    />
                    {errors.work_location && touched.work_location && (
                      <div className="text-red-500 text-xs sm:text-sm mt-1 text-left">
                        {errors.work_location}
                      </div>
                    )}
                  </div>
                  {timesheetValues?.status === "REJ" && (
                    <FormField
                      label="Rejection Remarks"
                      name="rejectionRemarks"
                      value={
                        timesheetValues?.rejectionRemarks ||
                        timesheetValues?.remarks ||
                        ""
                      }
                      disabled={true}
                      rows={2}
                      legend
                      className="w-full text-xs sm:text-sm"
                    />
                  )}
                </FormRow>

                {/* Dates and Hours */}
                <FormRow className="mt-4 flex-col sm:flex-row gap-2 sm:gap-4">
                  <div className="w-full relative">
                    <MuiDatePicker
                      label="Start Date"
                      value={
                        values?.startDate ? moment(values.startDate, "MM-DD-YYYY") : null
                      }
                      onChange={(date) => { 
                        if (date) {
                          const selectedProject = activeProjects.find(
                            (project) => project.id === values?.projectId
                          );
                          setFieldValue("startDate", date);
                          if (
                            values?.endDate &&
                            moment(date).isAfter(moment(values.endDate))
                          ) {
                            setFieldValue("endDate", date);
                          }
                        }
                      }}
                      required
                      disabled={!values?.projectId}
                      minDate={
                        values?.projectId
                          ? moment(
                              activeProjects.find(
                                (project) => project.id === values.projectId
                              )?.startDate
                            )
                          : null
                      }
                      maxDate={
                        values?.projectId
                          ? moment(
                              activeProjects.find(
                                (project) => project.id === values.projectId
                              )?.endDate
                            )
                          : null
                      }
                      className="text-xs sm:text-sm"
                    />
                    {errors.startDate && touched.startDate && (
                      <p className="mt-1 text-xs sm:text-sm text-rose-500 text-left">
                        {errors.startDate}
                      </p>
                    )}
                  </div>

                  <div className="w-full relative">
                    <MuiDatePicker
                      label="End Date"
                      value={values?.endDate ? moment(values.endDate, "MM-DD-YYYY") : null}
                      onChange={(date) => {
                        if (date) {
                          const selectedProject = activeProjects.find(
                            (project) => project.id === values?.projectId
                          );
                          if (
                            selectedProject &&
                            selectedProject.endDate &&
                            moment(date, "MM-DD-YYYY").isAfter(
                              moment(selectedProject.endDate, "MM-DD-YYYY")
                            )
                          ) {
                            toast.error("End date exceeds project end date");
                            return;
                          }
                          setFieldValue("endDate", date);
                        }
                      }}
                      disabled={!values?.projectId}
                      minDate={
                        values?.startDate
                          ? moment(values.startDate, "MM-DD-YYYY")
                          : values?.projectId
                          ? moment(
                              activeProjects.find(
                                (project) => project.id === values.projectId
                              )?.startDate
                            )
                          : null
                      }
                      maxDate={
                        values?.projectId
                          ? moment(
                              activeProjects.find(
                                (project) => project.id === values.projectId
                              )?.endDate
                            )
                          : moment().endOf("day")
                      }
                      className="text-xs sm:text-sm"
                      required
                    />
                    {errors.endDate && touched.endDate && (
                      <p className="mt-1 text-xs sm:text-sm text-rose-500 text-left">
                        {errors.endDate}
                      </p>
                    )}
                  </div>
                  <div className="w-full relative">
                    <FormField
                      type="number"
                      name="total_hours"
                      value={values?.total_hours}
                      onChange={handleChange}
                      error={errors.total_hours}
                      touched={touched.total_hours}
                      placeholder="Enter total hours"
                      // icon={<FaClock className="text-primary" />}
                      required
                      legend
                      className="w-full text-xs sm:text-sm"
                    />
                    {errors.total_hours && touched.total_hours && (
                      <div className="text-red-500 text-xs sm:text-sm mt-1 text-left">
                        {errors.total_hours}
                      </div>
                    )}
                  </div>
                </FormRow>

                {/* Holidays Section */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-700 text-sm sm:text-base">
                      Holidays
                    </h3>
                    <FormButton
                      type="button"
                      size="sm"
                      onClick={addHoliday}
                      icon={<FaPlus />}
                      className="bg-primary hover:bg-blue-700 text-white transition-colors px-3 py-1 text-xs sm:text-sm"
                    >
                      Add Holiday
                    </FormButton>
                  </div>

                  <div
                    className={`${
                      holidays.length > 2
                        ? "max-h-40 sm:max-h-48 overflow-y-auto pr-1"
                        : ""
                    } space-y-3`}
                  >
                    {holidays.length > 0 ? (
                      holidays.map((holiday, index) => (
                        <div
                          key={index}
                          className="p-3 border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow"
                        >
                          <FormRow
                            cols="1 sm:2"
                            gap={2}
                            className="flex-col sm:flex-row gap-2 sm:gap-4"
                          >
                            <div className="w-full relative">
                              <MuiDatePicker
                                label="Holiday Date"
                                value={
                                  holiday.employee_holiday_date
                                    ? moment(holiday.employee_holiday_date, "MM-DD-YYYY")
                                    : null
                                }
                                onChange={(date) => {
                                  if (date) {
                                    updateHoliday(
                                      index,
                                      "employee_holiday_date",
                                      date.format("MM-DD-YYYY")
                                    );
                                  }
                                }}
                                minDate={
                                  values?.startDate
                                    ? moment(values.startDate, "MM-DD-YYYY")
                                    : null
                                }
                                maxDate={
                                  values?.endDate
                                    ? moment(values.endDate, "MM-DD-YYYY")
                                    : null
                                }
                                className="text-xs sm:text-sm"
                              />
                              {errors.holidays &&
                                errors.holidays[index]?.date &&
                                touched.holidays &&
                                touched.holidays[index]?.date && (
                                  <div className="text-red-500 text-xs sm:text-sm mt-1">
                                    {errors.holidays[index].date}
                                  </div>
                                )}
                            </div>

                            <div className="w-full relative">
                              <FormField
                                type="text"
                                name={`holidays[${index}].notes`}
                                value={holiday.notes}
                                onChange={(e) =>
                                  updateHoliday(index, "notes", e.target.value)
                                }
                                placeholder="Enter holiday notes"
                                legend
                                className="w-full text-xs sm:text-sm"
                              />
                              <button
                                type="button"
                                className="absolute top-4 right-2 text-rose-500 hover:text-rose-700 transition-colors"
                                onClick={() => removeHoliday(index)}
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </div>
                          </FormRow>
                        </div>
                      ))
                    ) : (
                      <div className="text-center p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300 flex items-center justify-center">
                        <p className="text-gray-500 text-xs sm:text-sm">
                          Click the "Add Holiday" button to add holidays
                        </p>
                      </div>
                    )}
                  </div>

                  {/* File Upload Section */}
                  <div className="mt-4">
                    <h3 className="font-medium text-gray-700 text-sm sm:text-base mb-2">
                      Upload Files
                    </h3>

                    {/* Drop Zone */}
                    <div
                      className={`relative border-2 border-dashed rounded-lg transition-all duration-200 ${
                        isDragging
                          ? "border-primary bg-blue-50/50"
                          : "border-gray-300 hover:border-primary/50 bg-gray-50/30"
                      }`}
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                    >
                      {selectedFiles.length === 0 ? (
                        <div className="p-4 text-center">
                          <div className="flex justify-center mb-2">
                            <div
                              className={`p-2 rounded-full ${
                                isDragging ? "bg-primary/20" : "bg-gray-100"
                              }`}
                            >
                              <FaUpload
                                className={`w-5 h-5 ${
                                  isDragging ? "text-primary" : "text-gray-400"
                                }`}
                              />
                            </div>
                          </div>

                          <h4 className="text-sm font-medium text-gray-700 mb-1">
                            {isDragging ? "Drop files here" : ""}
                          </h4>

                          <p className="text-xs text-gray-500 mb-3">
                            Drag and drop files here, or click to browse
                          </p>

                          <label className="inline-flex items-center px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg hover:bg-primaryDark transition-colors cursor-pointer">
                            <FaUpload className="w-3 h-3 mr-1" />
                            Choose Files
                            <input
                              type="file"
                              multiple
                              className="hidden"
                              onChange={handleFileChange}
                            />
                          </label>
                        </div>
                      ) : (
                        <div className="p-3">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-gray-700">
                              Selected Files ({selectedFiles.length})
                            </h4>
                            <label className="inline-flex items-center px-2 py-1 bg-primary text-white text-xs font-medium rounded hover:bg-primaryDark transition-colors cursor-pointer">
                              <FaUpload className="w-3 h-3 mr-1" />
                              Add More
                              <input
                                type="file"
                                multiple
                                className="hidden"
                                onChange={handleFileChange}
                              />
                            </label>
                          </div>

                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {selectedFiles.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                              >
                                <div className="flex items-center space-x-2 flex-1 min-w-0">
                                  <div className="flex-shrink-0">
                                    <FaFile className="w-4 h-4 text-blue-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-gray-900 truncate">
                                      {file.name || file?.fileName}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {formatFileSize(file.size || 0)}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteFile(index)}
                                  className="flex-shrink-0 ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                                  title="Remove file"
                                >
                                  <FaTimes className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Submit Button - Sticky on Mobile */}
                <div className="sticky bottom-0 bg-white p-4 sm:p-0 mt-4 pb-4 sm:pb-0 flex justify-end">
                  <FormButton
                    type="submit"
                    disabled={isLoading || isSubmitting}
                    className={`px-4 py-2 h-10 w-full sm:w-40 rounded-lg text-white transition text-xs sm:text-sm
                      ${
                        isLoading || isSubmitting
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-primary hover:bg-primaryDark cursor-pointer"
                      }`}
                  >
                    {isLoading || isSubmitting ? "Submitting..." : "Submit"}
                  </FormButton>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </ModalComponent>
  );
};

export default TimeSheet;
